/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import ConvItemRenderer from "./ConvItemRenderer";
import C from "../../../constants/cockpit.js";
import cockpitActions from "../../../redux/actions/cockpitActions";
import {
	AutoSizer,
	CellMeasurer,
	CellMeasurerCache,
	InfiniteLoader,
	List as VirtualList
} from "react-virtualized";
import { useDispatch, useSelector } from "react-redux";
import { getConversationTypeByLeftTab } from "helpers/utilities";
import ConvItemRendererCOLD from "./cold/ConvItemRendererCOLD";
import { t } from "i18next";
import { IconButton, Tooltip } from "@material-ui/core";
import { ArrowDownward } from "@material-ui/icons";

const cache = new CellMeasurerCache({
	fixedWidth: true,
	defaultHeight: 60
});

var loadMoreRowsTimer = null;
const loadMoreRowsDelay = 1500;
var loadMoreRowsStart = 0;

const DEFAULT_NUMBER_OF_CONVERSATIONS = 9;

// Set to true to disable automatic load on scroll (for testing manual button)
const DISABLE_AUTO_SCROLL_LOAD = false;

const LoadingCell = ({
	index,
	parent,
	key,
	style,
	isLoadMoreRow,
	isLoadingMore,
	handleManualLoadMore
}) => {
	const [temporaryDisabled, setTemporaryDisabled] = useState(true);

	useEffect(() => {
		setTemporaryDisabled(true);
		if (isLoadMoreRow) {
			const timer = setTimeout(() => {
				setTemporaryDisabled(false);
			}, 3000); // 500 ms delay

			return () => clearTimeout(timer); // Cleanup on unmount or re-render
		}
	}, [isLoadMoreRow]);

	return (
		<CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
			{({ measure, registerChild }) => (
				<div ref={registerChild} style={style}>
					<Box
						p={2}
						display="flex"
						justifyContent="center"
						alignItems="center"
						borderTop="1px solid #e0e0e0"
						height={48}
					>
						{(isLoadingMore || temporaryDisabled) && <CircularProgress size={24} />}
						{!isLoadingMore && !temporaryDisabled && (
							<Tooltip arrow placement="top" title={t("CONVERSATIONS.LIST.LOAD_MORE.loadMore")}>
								<IconButton
									size="medium"
									onClick={() => {
										handleManualLoadMore();
										// Re-measure after loading to update the row height
										setTimeout(() => measure(), 100);
									}}
								>
									<ArrowDownward fontSize="medium" />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				</div>
			)}
		</CellMeasurer>
	);
};

const renderRow = (
	index,
	parent,
	key,
	style,
	conversations,
	handleSelectConversation,
	isCold,
	showLoadMoreButton,
	handleManualLoadMore,
	isLoadingMore
) => {
	// Check if this is the last row and should be the "Load More" button
	const isLoadMoreRow = showLoadMoreButton && index === conversations.length;

	if (isLoadMoreRow) {
		return (
			<LoadingCell
				index={index}
				parent={parent}
				key={key}
				style={style}
				isLoadMoreRow={isLoadMoreRow}
				isLoadingMore={isLoadingMore}
				handleManualLoadMore={handleManualLoadMore}
			/>
		);
	}

	return (
		<CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
			{({ measure, registerChild }) => (
				<div ref={registerChild} style={style}>
					{isCold ? (
						<ConvItemRendererCOLD
							measure={measure}
							conv={conversations[index]}
							handleSelectConversation={handleSelectConversation}
						/>
					) : (
						<ConvItemRenderer
							measure={measure}
							conv={conversations[index]}
							handleSelectConversation={handleSelectConversation}
						/>
					)}
				</div>
			)}
		</CellMeasurer>
	);
};

/**
 * List of all conversations with scroll pagination.
 * Here we send filters in the back for the query, sort fields are specified in cockpitActions.getScrollPaginatedConversations
 */
const Convs = ({ handleSelectConversation }) => {
	const listRef = useRef();
	const infiniteLoaderRef = useRef();
	const dispatch = useDispatch();
	/**
	 * PROPS
	 */
	const {
		conversations,
		selectedConversation,
		scrollToSelectedConversation,
		leftTab,
		convAscFilter,
		convAgentFilter,
		convSupervisorFilter,
		convSearchFilter,
		convIntentFilter,
		convGroupFilter,
		convUserFilter,
		convStartDateFilter,
		convEndDateFilter,
		convPriorityFilter,
		convSentimentFilter,
		convSubstateFilter,
		convConnectedFilter,
		convEscalationFilter,
		convLanguageFilter,
		convChannelFilter,
		isCockpitArchived,
		isCold,
		nbCurrentConvs,
		convsReload,
		convDirectionFilter,
		convContactFilter,
		convPathContextFilter,
		convNoBotFilter
	} = useSelector(state => state.cockpit);
	const { auth } = useSelector(state => state.userStatus);
	const selectedAssistantID = useSelector(state => state.selectedAssistantID);
	/**
	 * OTHER VARS
	 */
	const prevUserRef = useRef();
	const prevIntentRef = useRef();
	const prevGroupRef = useRef();

	/**
	 * 0 - Idle
	 * 1 - First load 0 to DEFAULT_NUMBER_OF_CONVERSATIONS
	 * 2 - Complete the list to fit the screen
	 * 3 - Enable scroll
	 */
	const [fetchConversationStep, setFetchConversationStep] = useState(0);
	const [changeAction, setChangeAction] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const conversationsContainer = useRef(null);

	const leftTabType = getConversationTypeByLeftTab(leftTab, isCockpitArchived);
	const totalCount = nbCurrentConvs[0] === leftTabType ? nbCurrentConvs[1] : 0;

	/**
	 * On assistantID or tab change
	 */
	useEffect(async () => {
		if (selectedAssistantID) {
			await resetLoad("leftTab, selectedAssistantID", true);
		}
	}, [leftTab, selectedAssistantID]);

	/**
	 * To reload from ConvsContainerTabs, on Tab click
	 */
	useEffect(async () => {
		if (convsReload) {
			dispatch({ type: C.SET_CONVS_RELOAD, payload: false });
			await resetLoad("convsReload");
		}
	}, [convsReload]);

	/**
	 * On filters change
	 */
	useEffect(async () => {
		if (auth?.user) {
			await resetLoad("many filters");
		}
	}, [
		convSearchFilter,
		convChannelFilter,
		convEscalationFilter,
		convLanguageFilter,
		convPriorityFilter,
		convSentimentFilter,
		convStartDateFilter,
		convEndDateFilter,
		convAgentFilter,
		convSupervisorFilter,
		convSubstateFilter,
		convConnectedFilter,
		convDirectionFilter,
		convContactFilter,
		convPathContextFilter,
		convNoBotFilter
	]);

	// ------- To avoid useless multiple renders
	// -------
	/** On INTENT change if filter is ON */
	useEffect(async () => {
		if (prevIntentRef.current && prevIntentRef.current !== convIntentFilter) {
			await resetLoad("convIntentFilter");
		}
		prevIntentRef.current = convIntentFilter;
	}, [convIntentFilter]);

	/** On USER change if filter is ON */
	useEffect(async () => {
		if (prevUserRef.current && prevUserRef.current !== convUserFilter) {
			await resetLoad("convUserFilter");
		}
		prevUserRef.current = convUserFilter;
	}, [convUserFilter]);

	/** On DIRECTION change if filter is SELECTED */
	useEffect(async () => {
		if (typeof convDirectionFilter === "string") {
			await resetLoad("convDirectionFilter");
		}
	}, [convDirectionFilter]);

	/** On CONTACT change if filter is SELECTED */
	useEffect(async () => {
		if (typeof convContactFilter === "string") {
			await resetLoad("convContactFilter");
		}
	}, [convContactFilter]);

	/** On DIRECTION change if filter is SELECTED */
	useEffect(async () => {
		if (!convPathContextFilter) {
			await resetLoad("convPathContextFilter");
		}
	}, [convPathContextFilter]);

	/** On GROUP change if filter is ON */
	useEffect(async () => {
		if (prevGroupRef.current && prevGroupRef.current !== convGroupFilter) {
			await resetLoad("convGroupFilter");
		}
		prevGroupRef.current = convGroupFilter;
	}, [convGroupFilter]);

	/** On ASC change*/
	useEffect(async () => {
		if (typeof convAscFilter === "boolean") {
			await resetLoad("convAscFilter");
		}
	}, [convAscFilter]);
	// -------
	// -------

	/**
	 *
	 */
	useEffect(() => {
		if (scrollToSelectedConversation) {
			const index = conversations
				.map(conv => conv.header.fID)
				?.indexOf(selectedConversation?.header?.fID);
			if (index >= 0) {
				listRef.current?.scrollToRow(index);
			}
			dispatch({
				type: C.CLEAR_SCROLL_TO_SELECTED_CONVERSATION
			});
		}
	}, [scrollToSelectedConversation]);

	/**
	 *
	 * @returns
	 */
	function isRowLoaded({ index }) {
		return !!conversations[index];
	}

	/**
	 * This function is called each time we change filters, assistantID or tab
	 */
	const resetLoad = async (origin, bypass = false) => {
		// Reset the step
		setFetchConversationStep(0);
		// if there is a timer, clean it ...
		if (loadMoreRowsTimer !== null) {
			// console.log("PIKA -> resetLoad timer cleaned -", origin);
			clearTimeout(loadMoreRowsTimer);
		}
		// ... and recreate it, until ...
		// console.log("PIKA -> resetLoad wait -", origin);
		loadMoreRowsTimer = setTimeout(async () => {
			// console.log("PIKA -> resetLoad doing the call -", origin);
			// timer expired and then call the function
			await loadMoreRows({
				startIndex: 0,
				stopIndex: DEFAULT_NUMBER_OF_CONVERSATIONS,
				count: true,
				bypass
			});

			// Set the step to 1
			setFetchConversationStep(1);
			setChangeAction(!changeAction);
			// console.log("PIKA -> resetLoad called and timer cleaned -", origin);
			loadMoreRowsTimer = null;
		}, loadMoreRowsDelay);
	};

	/**
	 * Use effect to complete the conversations list on mount
	 * We load ${DEFAULT_NUMBER_OF_CONVERSATIONS} conversations on mount, but if we have a little screen, we need to load more
	 * else we can't scroll into the conversations list and we can't load more rows ...
	 *
	 * This useEffect is called only after resetLoad() has been called once
	 */
	useEffect(() => {
		setIsLoadingMore(false);
		// When the conversations list is full, we don't need to load more rows
		if (fetchConversationStep < 1 || conversations.length >= totalCount) {
			return;
		}

		// Only proceed if we're in step 1 or 2 (not 3, which means scroll is enabled)
		if (fetchConversationStep === 1) {
			setFetchConversationStep(2);
		}

		// Use requestAnimationFrame for better DOM measurement timing
		const checkAndLoadMore = () => {
			requestAnimationFrame(() => {
				// Get the loaded conversations list container's height
				const innerScrollContainerHeight = document.querySelector(
					".ReactVirtualized__Grid__innerScrollContainer"
				)?.clientHeight;

				// Get the conversations list container's height
				const conversationsContainerHeight = conversationsContainer.current?.clientHeight;

				if (!innerScrollContainerHeight || !conversationsContainerHeight) {
					// Retry if DOM elements aren't ready yet
					setTimeout(checkAndLoadMore, 100);
					return;
				}

				// If the loaded conversations list container's height is smaller than the conversations list container's height
				// we need to load more rows
				if (innerScrollContainerHeight <= conversationsContainerHeight + 200) {
					// Only load more if we haven't reached the total count
					if (conversations.length < totalCount) {
						const batchSize = 10; // Load more conversations at once
						const nextStopIndex = Math.min(conversations.length + batchSize, totalCount);

						// Load more rows
						loadMoreRows({
							startIndex: conversations.length,
							stopIndex: nextStopIndex,
							count: false,
							bypass: true
						});
						// The effect will re-trigger when conversations.length changes
					} else {
						// All conversations loaded, enable scroll
						setFetchConversationStep(3);
					}
				} else {
					// Enough rows to enable scrolling
					setFetchConversationStep(3);
				}
			});
		};

		if (fetchConversationStep === 2) {
			checkAndLoadMore();
		}
	}, [conversations.length, changeAction, fetchConversationStep, totalCount]);

	/**
	 * This function is called onMount and onScroll by InfiniteLoader (cf below)
	 */
	async function loadMoreRows({ startIndex, stopIndex, count, bypass = false }) {
		if (selectedAssistantID) {
			const tab = getConversationTypeByLeftTab(leftTab, isCockpitArchived);

			const limit = stopIndex - startIndex;
			if (limit > 0) {
				if (isCold && isCockpitArchived) {
					await dispatch(cockpitActions.getScrollPaginatedColdConversations(limit, startIndex));
				} else if (tab !== "cold") {
					// console.log(
					// 	"PIKA -> calling getScrollPaginatedConversations with params",
					// 	JSON.stringify({ tab, limit, startIndex, count })
					// );
					const now = new Date().getTime();
					if (now - loadMoreRowsStart > loadMoreRowsDelay || bypass) {
						// console.log("PIKA -> loadMoreRows - in between calls is", now - loadMoreRowsStart);
						await dispatch(
							cockpitActions.getScrollPaginatedConversations(tab, limit, startIndex, count)
						);
						loadMoreRowsStart = now;
					}
				}
			}
		}
	}

	/**
	 * Manual load more button handler
	 */
	const handleManualLoadMore = async () => {
		setIsLoadingMore(true);
		const batchSize = 10;
		const nextStopIndex = Math.min(conversations.length + batchSize, totalCount);

		await loadMoreRows({
			startIndex: conversations.length,
			stopIndex: nextStopIndex,
			count: false,
			bypass: true
		});
	};

	// Check if there are more conversations to load
	const hasMoreConversations = conversations.length < totalCount;
	const showLoadMoreButton = hasMoreConversations && fetchConversationStep >= 1;

	// Calculate row count: conversations + 1 for load more button if needed
	const virtualListRowCount = showLoadMoreButton ? conversations.length + 1 : conversations.length;

	return (
		<Box height="100%" data-component-name="Convs" ref={conversationsContainer}>
			<InfiniteLoader
				ref={infiniteLoaderRef}
				isRowLoaded={isRowLoaded}
				loadMoreRows={event => {
					// Request only if user has scroll
					// This function is called only after resetLoad() has been called once
					if (fetchConversationStep === 3 && !DISABLE_AUTO_SCROLL_LOAD) {
						return loadMoreRows({ ...event, bypass: true });
					}
					return Promise.resolve();
				}}
				rowCount={totalCount + 1}
				minimumBatchSize={10} // How many (minimum) rows to load at a time (default 10)
				threshold={3} // How many rows before the end to trigger the loadMoreRows function (default 15)
			>
				{({ onRowsRendered, registerChild }) => (
					<AutoSizer>
						{({ height, width }) => (
							<VirtualList
								ref={ref => {
									registerChild(ref);
									listRef.current = ref;
								}}
								width={width}
								height={height}
								rowCount={virtualListRowCount}
								deferredMeasurementCache={cache}
								rowHeight={cache.rowHeight}
								onRowsRendered={onRowsRendered}
								rowRenderer={({ key, index, style, parent }) =>
									renderRow(
										index,
										parent,
										key,
										style,
										conversations,
										handleSelectConversation,
										isCold,
										showLoadMoreButton,
										handleManualLoadMore,
										isLoadingMore
									)
								}
							/>
						)}
					</AutoSizer>
				)}
			</InfiniteLoader>
		</Box>
	);
};
export default Convs;
